import React, { useState, useEffect } from 'react';
import { Facebook, Instagram, Mail, Phone, MapPin } from 'lucide-react';
import api from '@/utils/api';

// Custom TikTok icon (lucide-react doesn't have it)
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

const Footer = ({ teacherId }) => {
  const [teacherInfo, setTeacherInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (teacherId) {
      fetchTeacherInfo();
    } else {
      // Try to get the first active teacher for the landing page
      fetchDefaultTeacher();
    }
  }, [teacherId]);

  const fetchTeacherInfo = async () => {
    try {
      // Fetch teacher profile for social links
      const response = await api.get(`/auth/user/${teacherId}`);
      setTeacherInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch teacher info:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDefaultTeacher = async () => {
    try {
      // Get teachers with social links for the footer
      const response = await api.get('/teachers/featured');
      if (response.data && response.data.length > 0) {
        // Find a teacher with social links
        const teacherWithSocials = response.data.find(t => 
          t.facebook_url || t.instagram_url || t.tiktok_url
        );
        if (teacherWithSocials) {
          setTeacherInfo(teacherWithSocials);
        }
      }
    } catch (error) {
      // Silently fail - footer will just not show social links
    } finally {
      setLoading(false);
    }
  };

  const hasSocialLinks = teacherInfo && (
    teacherInfo.facebook_url || 
    teacherInfo.instagram_url || 
    teacherInfo.tiktok_url
  );

  return (
    <footer 
      className="w-full py-8 mt-auto"
      style={{ backgroundColor: '#163329' }}
    >
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          {/* Logo/Brand */}
          <div className="text-center md:text-left">
            <h3 
              className="text-xl font-bold text-white"
              style={{ fontFamily: 'Spectral, serif' }}
            >
              Insight Evolution
            </h3>
            <p className="text-sm mt-1" style={{ color: '#E3E1DB' }}>
              Home Skill Learning and Development Solutions
            </p>
          </div>

          {/* Social Links */}
          {hasSocialLinks && (
            <div className="flex items-center gap-4">
              <span className="text-sm" style={{ color: '#E3E1DB' }}>Follow Us:</span>
              <div className="flex items-center gap-3">
                {teacherInfo.facebook_url && (
                  <a
                    href={teacherInfo.facebook_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-full transition-all hover:scale-110"
                    style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                    aria-label="Facebook"
                    data-testid="social-facebook"
                  >
                    <Facebook className="h-5 w-5 text-white" />
                  </a>
                )}
                {teacherInfo.instagram_url && (
                  <a
                    href={teacherInfo.instagram_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-full transition-all hover:scale-110"
                    style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                    aria-label="Instagram"
                    data-testid="social-instagram"
                  >
                    <Instagram className="h-5 w-5 text-white" />
                  </a>
                )}
                {teacherInfo.tiktok_url && (
                  <a
                    href={teacherInfo.tiktok_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-full transition-all hover:scale-110"
                    style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                    aria-label="TikTok"
                    data-testid="social-tiktok"
                  >
                    <TikTokIcon className="h-5 w-5 text-white" />
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Contact Info */}
          <div className="flex flex-col items-center md:items-end gap-2">
            <a 
              href="mailto:ieskilldev@gmail.com"
              className="flex items-center gap-2 text-sm hover:opacity-80 transition-opacity"
              style={{ color: '#E3E1DB' }}
            >
              <Mail className="h-4 w-4" />
              ieskilldev@gmail.com
            </a>
          </div>
        </div>

        {/* Bottom Bar */}
        <div 
          className="mt-6 pt-6 border-t flex flex-col md:flex-row justify-between items-center gap-4"
          style={{ borderColor: 'rgba(255,255,255,0.1)' }}
        >
          <p className="text-xs" style={{ color: '#E3E1DB' }}>
            © {new Date().getFullYear()} Insight Evolution. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-xs" style={{ color: '#E3E1DB' }}>
            <a href="/legal?section=terms" className="hover:text-white transition-colors" data-testid="footer-terms-link">
              Terms of Service
            </a>
            <a href="/legal?section=privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">
              Privacy Policy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
